import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { getCourseSection1DataApi, addCourseSection1DataApi, deleteCourseSection1DataApi } from 'Services/allApi';
import { MdDelete } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CourseSection1 = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const cardTitle = location.state?.cardTitle || "Default Title";
    const [sectionData, setSectionData] = useState([]);
    const [courseId, setCourseId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [newContent, setNewContent] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleEditClick = (id) => {
        navigate(`/admin/edit-courses/${id}`);
    };

    const handleModulesClick = (id) => {
        navigate(`/admin/modules-courses/${id}`, { state: { cardTitle: location.state?.cardTitle } });
    };

    const handleProgramClick = (id) => {
        navigate(`/admin/program-courses/${id}`, { state: { cardTitle: location.state?.cardTitle } });
    };

    const handleSupportClick = (id) => {
        navigate(`/admin/support-courses/${id}`, { state: { cardTitle: location.state?.cardTitle } });
    };

    const handleSection2Click = (id) => {
        navigate(`/admin/section2-courses/${id}`, { state: { cardTitle: location.state?.cardTitle } });
    };

    useEffect(() => {
        const fetchSectionData = async () => {
            try {
                const response = await getCourseSection1DataApi(id);
                if (response.error) {
                    console.error("Error fetching course section data:", response.message);
                    toast.error("Error fetching course section data");
                } else {
                    setSectionData(response.data || []);
                    if (response.data && response.data.length > 0) {
                        setCourseId(response.data[0].course);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Error fetching data");
            } finally {
                setLoading(false);
            }
        };

        fetchSectionData();
    }, [id]);

    const handleNewContentChange = (e) => {
        setNewContent(e.target.value);
        setErrorMessage('');
    };

    const handleAddSection = async () => {
        if (!newContent.trim()) {
            setErrorMessage('Content cannot be empty');
            toast.error('Content cannot be empty');
            return;
        }

        if (!courseId) {
            console.error("Missing course ID");
            toast.error('Missing course ID');
            return;
        }

        const newSectionItem = {
            content: newContent,
            course: courseId
        };

        try {
            const response = await addCourseSection1DataApi(newSectionItem);
            if (response.error) {
                console.error("Error adding course section data:", response.message);
                toast.error("Error adding course section data");
            } else {
                setSectionData([...sectionData, response.data]);
                setShowModal(false);
                setNewContent('');
                toast.success("Section added successfully");
            }
        } catch (error) {
            console.error("Error creating course section data:", error);
            toast.error("Error creating course section data");
        }
    };

    const handleDeleteSection = async (sectionId) => {
        try {
            const response = await deleteCourseSection1DataApi(sectionId);
            if (response.error) {
                console.error("Error deleting course section data:", response.message);
                toast.error("Error deleting course section data");
            } else {
                setSectionData(sectionData.filter((item) => item.id !== sectionId));
                toast.success("Course section data deleted successfully");
            }
        } catch (error) {
            console.error("Error deleting course section data:", error);
            toast.error("Error deleting course section data");
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto p-8 bg-white shadow-lg rounded-lg  dark:!bg-navy-700 dark:text-white">
            <ToastContainer />
            <h2 className="text-4xl font-semibold mb-6">{cardTitle}</h2>
            <div className="flex mb-6 justify-between">
                <div className="space-x-4">
                    <button
                        onClick={() => handleEditClick(id)}
                        className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700"
                    >
                        Home
                    </button>
                    <button
                        onClick={() => handleModulesClick(id)}
                        className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700"
                    >
                        Modules
                    </button>
                    <button onClick={() => handleProgramClick(id)} className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700">
                        Program
                    </button>
                    <button onClick={() => handleSupportClick(id)} className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700">
                        Support
                    </button>
                    <button className="bg-purple-500 text-white px-4 py-2 rounded-md">
                         Section 1
                    </button>
                    <button onClick={() => handleSection2Click(id)} className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700">
                        Section 2
                    </button>
                </div>
                <div>
                    <button
                        onClick={() => setShowModal(true)}
                        className="bg-purple-800 hover:bg-green-600 text-white px-4 py-2 rounded-md"
                    >
                        Add Section
                    </button>
                </div>
            </div>

            {loading && (
                <div className="text-center">
                    <p className="text-lg">Loading...</p>
                </div>
            )}

            {!loading && sectionData.length === 0 && (
                <div className="text-center">
                    <p className="text-lg text-purple-800">No course section data to display</p>
                </div>
            )}

            {!loading && sectionData.length > 0 && (
                <div className="mt-6">
                    {sectionData.map((item, index) => (
                        <div key={index} className="mb-6 p-6 border border-gray-200 rounded-lg shadow-md dark:border-gray-600 dark:bg-[#0b14374d]">
                            <h3 className="text-xl font-bold mb-4">Section Item {index + 1}</h3>
                            {/* Display content as plain text */}
                            <p className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-900 dark:bg-[#0b14374d] dark:text-white">
                                {item.content}
                            </p>
                            <div className="flex justify-end space-x-4 mt-4">
                                <button
                                    onClick={() => handleDeleteSection(item.id)}
                                    className="rounded-md border-2 border-red-600 px-3 py-2 font-semibold text-red-600 shadow-sm hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-600 flex items-center"
                                >
                                    <MdDelete className="mr-2" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-xl w-1/2 dark:bg-gray-800 dark:text-white">
                        <h3 className="text-2xl font-bold mb-4">Add Section</h3>
                        <input
                            type="text"
                            placeholder="Enter section content"
                            value={newContent}
                            onChange={handleNewContentChange}
                            className="w-full px-4 py-2 border rounded-lg mb-4 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                        />
                        {errorMessage && (
                            <p className="text-red-600 text-sm m-2">{errorMessage}</p>
                        )}
                        <div className="flex justify-between">
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setErrorMessage('');
                                }}
                                className="bg-gray-500 text-white px-4 py-2 rounded-md"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddSection}
                                className="bg-green-600 text-white px-4 py-2 rounded-md"
                            >
                                Add Section
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseSection1;